import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdPerson, MdLock, MdLogin, MdBusiness } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const Login = () => {
  // When mounted under /c/:slug/login, useParams() exposes the slug from the
  // URL. That mode locks the company field (read-only) and shows the friendly
  // company name in the heading.
  const { slug: urlSlug } = useParams();
  const lockedToSlug = !!urlSlug;

  const [company, setCompany]   = useState(urlSlug || '');
  const [companies, setCompanies] = useState([]);   // { slug, name }[] of active tenants
  const [lockedCompanyName, setLockedCompanyName] = useState(''); // friendly name when locked
  const [lockedSlugInvalid, setLockedSlugInvalid] = useState(false); // true if /c/:slug is unknown
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Pre-fetch the public list of company slugs so a returning user can pick
  // theirs from a datalist instead of remembering the spelling. The endpoint
  // exposes ONLY slug + name — no tenant data. Skipped when locked to a
  // specific slug via /c/<slug>/login (no need for a picker).
  useEffect(() => {
    if (lockedToSlug) return;
    api.get('/auth/companies')
      .then(r => setCompanies(r.data || []))
      .catch(() => {});
  }, [lockedToSlug]);

  // When the URL pins a slug (/c/<slug>/login), validate it and load the
  // friendly name. Unknown / suspended slug shows an inline error and
  // disables the form so we don't dispatch a doomed login request.
  useEffect(() => {
    if (!lockedToSlug) return;
    setCompany(urlSlug.toLowerCase());
    api.get(`/auth/companies/${encodeURIComponent(urlSlug)}`)
      .then(r => {
        setLockedCompanyName(r.data?.name || urlSlug);
        setLockedSlugInvalid(false);
      })
      .catch(() => {
        setLockedSlugInvalid(true);
      });
  }, [lockedToSlug, urlSlug]);

  // Restore last successfully-used company from localStorage so a returning
  // user on a shared kiosk doesn't have to retype it. Skipped when the URL
  // already pins a slug — that takes precedence.
  useEffect(() => {
    if (lockedToSlug) return;
    const saved = localStorage.getItem('identium_last_company');
    if (saved) setCompany(saved);
  }, [lockedToSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (lockedSlugInvalid) {
      setError('This company link is invalid. Please use your normal login URL.');
      return;
    }
    if (!username || !password) { setError('Please enter username and password'); return; }
    setLoading(true);
    try {
      await login(username, password, company.trim().toLowerCase() || undefined);
      if (company.trim()) localStorage.setItem('identium_last_company', company.trim().toLowerCase());
      navigate('/dashboard');
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'COMPANY_REQUIRED') {
        setError('Multiple companies are configured. Please pick yours from the Company field.');
      } else {
        setError(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #351232 0%, #5B2150 45%, #A32770 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, position: 'relative', overflow: 'hidden',
    }}>
      {/* Background pattern */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.04,
        backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, white 40px, white 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, white 40px, white 41px)',
      }} />

      {/* Floating circles */}
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: [120, 200, 80, 160, 100][i],
          height: [120, 200, 80, 160, 100][i],
          borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.06)',
          top: ['10%', '60%', '80%', '20%', '45%'][i],
          left: ['5%', '70%', '15%', '80%', '45%'][i],
          pointerEvents: 'none',
        }} />
      ))}

      <div style={{
        background: 'white', borderRadius: 20,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        width: '100%', maxWidth: 440, overflow: 'hidden',
        position: 'relative',
      }}>
        {/* Top accent */}
        <div style={{ height: 5, background: 'linear-gradient(90deg, #5B2150, #A32770, #5B2150)' }} />

        <div style={{ padding: '40px 40px 36px' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <img
              src={`${process.env.PUBLIC_URL}/logo-tagmyassets.png`}
              alt="Tag My Assets"
              style={{ width: '100%', maxWidth: 240, height: 'auto', marginBottom: 8 }}
            />
            <p style={{ color: '#78909c', fontSize: 13, fontWeight: 500 }}>
              {lockedToSlug && lockedCompanyName
                ? <>Sign in to <strong style={{ color: '#5B2150' }}>{lockedCompanyName}</strong></>
                : 'RFID Asset Management System'}
            </p>
          </div>

          {lockedSlugInvalid && (
            <div className="alert alert-danger" style={{ marginBottom: 20 }}>
              This company link is invalid. Use your normal login URL or check the spelling of "{urlSlug}".
            </div>
          )}

          {error && (
            <div className="alert alert-danger" style={{ marginBottom: 20 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Company field. Two rendering modes:
                  1. Locked (URL is /<slug>/login) — field is hidden; the
                     company is shown only in the heading ("Sign in to
                     <Name>"). The slug lives in `company` state (set by
                     the urlSlug effect above) and ships with the submit
                     payload, so we don't need a visible input at all.
                  2. Picker — no URL pin and more than one tenant exists:
                     text input + datalist autocomplete. Single-tenant
                     "default" installs render nothing here (backend
                     resolves implicitly). */}
            {!lockedToSlug && (companies.length > 1 || (companies.length === 1 && companies[0].slug !== 'default')) && (
              <div style={{ marginBottom: 18 }}>
                <label style={{
                  display: 'block', fontSize: 11, fontWeight: 700,
                  color: '#546e7a', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 7,
                }}>Company</label>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  border: '1.5px solid #e0e6ef', borderRadius: 10, overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '0 14px', color: '#90a4ae', fontSize: 18,
                    borderRight: '1.5px solid #e0e6ef', display: 'flex', alignItems: 'center',
                    background: '#f8fafc', height: 46,
                  }}>
                    <MdBusiness />
                  </div>
                  <input
                    type="text"
                    list="company-options"
                    value={company}
                    onChange={e => setCompany(e.target.value)}
                    placeholder="your company"
                    autoComplete="organization"
                    style={{
                      flex: 1, border: 'none', outline: 'none', padding: '12px 14px',
                      fontSize: 14, fontFamily: 'inherit', background: 'white',
                    }}
                  />
                  <datalist id="company-options">
                    {companies.map(c => (
                      <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                  </datalist>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 18 }}>
              <label style={{
                display: 'block', fontSize: 11, fontWeight: 700,
                color: '#546e7a', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 7,
              }}>
                Username
              </label>
              <div style={{
                display: 'flex', alignItems: 'center',
                border: '1.5px solid #e0e6ef', borderRadius: 10,
                overflow: 'hidden', transition: 'border-color 0.2s',
              }}
                onFocus={() => {}}
              >
                <div style={{
                  padding: '0 14px', color: '#90a4ae', fontSize: 18,
                  borderRight: '1.5px solid #e0e6ef', display: 'flex', alignItems: 'center',
                  background: '#f8fafc', height: 46,
                }}>
                  <MdPerson />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  autoComplete="username"
                  style={{
                    flex: 1, border: 'none', outline: 'none', padding: '12px 14px',
                    fontSize: 14, fontFamily: 'inherit', background: 'white',
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                <label style={{
                  fontSize: 11, fontWeight: 700,
                  color: '#546e7a', textTransform: 'uppercase', letterSpacing: '0.8px',
                }}>
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: '#5B2150', fontSize: 11, fontWeight: 600,
                  }}
                >Forgot password?</button>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center',
                border: '1.5px solid #e0e6ef', borderRadius: 10, overflow: 'hidden',
              }}>
                <div style={{
                  padding: '0 14px', color: '#90a4ae', fontSize: 18,
                  borderRight: '1.5px solid #e0e6ef', display: 'flex', alignItems: 'center',
                  background: '#f8fafc', height: 46,
                }}>
                  <MdLock />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  style={{
                    flex: 1, border: 'none', outline: 'none', padding: '12px 14px',
                    fontSize: 14, fontFamily: 'inherit', background: 'white',
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || lockedSlugInvalid}
              style={{
                width: '100%', padding: '14px',
                background: (loading || lockedSlugInvalid) ? '#90a4ae' : 'linear-gradient(135deg, #5B2150, #A32770)',
                color: 'white', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: (loading || lockedSlugInvalid) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: (loading || lockedSlugInvalid) ? 'none' : '0 4px 16px rgba(26,60,110,0.35)',
                transition: 'all 0.2s', letterSpacing: '0.5px',
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 16, height: 16, border: '2px solid white',
                    borderTopColor: 'transparent', borderRadius: '50%',
                    display: 'inline-block', animation: 'spin 0.8s linear infinite',
                  }} />
                  Signing In...
                </>
              ) : (
                <>
                  <MdLogin style={{ fontSize: 18 }} />
                  Sign In
                </>
              )}
            </button>
          </form>

        </div>

        <div style={{
          padding: '14px 40px', background: '#f8fafc',
          borderTop: '1px solid #e0e6ef', textAlign: 'center',
          fontSize: 11, color: '#90a4ae',
        }}>
          Tag My Assets &copy; 2026
        </div>
      </div>

      {/* Forgot password modal — operator-friendly explainer of the
          admin-reset flow. We don't run a self-service email reset (no
          public reset endpoint) on purpose: this is an enterprise tool
          and admins handle credential rotation. */}
      {forgotOpen && (
        <div
          onClick={() => setForgotOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 16, maxWidth: 460, width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
            }}
          >
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e0e6ef' }}>
              <h2 style={{ margin: 0, fontSize: 18, color: '#5B2150' }}>Forgot your password?</h2>
            </div>
            <div style={{ padding: '20px 24px', fontSize: 14, color: '#37474f', lineHeight: 1.6 }}>
              <p style={{ marginTop: 0 }}>
                This system uses <strong>admin-reset</strong> for password recovery.
                A self-service email link isn't enabled.
              </p>
              <p>To get back in:</p>
              <ol style={{ paddingLeft: 20 }}>
                <li>Contact one of your <strong>system admins</strong>.</li>
                <li>They'll open <em>User Management</em>, find your user, and click the <em>Reset Password</em> button.</li>
                <li>You'll get a one-time temporary password to sign in with.</li>
                <li>The system will force you to set a new password immediately after login.</li>
              </ol>
              <p style={{ fontSize: 12, color: '#78909c', marginBottom: 0 }}>
                If you <em>are</em> the admin and can't get in, restore the database from your last
                backup (or reset the admin user via the <code>ADMIN_SEED_*</code> env vars on the server).
              </p>
            </div>
            <div style={{ padding: '14px 24px', background: '#f8fafc', textAlign: 'right' }}>
              <button
                onClick={() => setForgotOpen(false)}
                style={{
                  background: '#5B2150', color: 'white', border: 'none',
                  padding: '10px 20px', borderRadius: 8, fontSize: 14,
                  fontWeight: 600, cursor: 'pointer',
                }}
              >Got it</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Login;
